import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react'
import { Grid, FormControlLabel, Radio, RadioGroup, Card, CardContent, Button, useTheme, Select, MenuItem, FormHelperText, darken, ToggleButton, ToggleButtonGroup, CircularProgress, lighten } from '@mui/material';

import { grey } from '@mui/material/colors';

import { useNavigate } from 'react-router-dom';

import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import AdfScannerRoundedIcon from '@mui/icons-material/AdfScannerRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import KeyboardRoundedIcon from '@mui/icons-material/KeyboardRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';


// lottie animation
import Lottie from '../../../utils/Lotties';
import ScanningAnimation from '../../../assets/animations/scanning-lottie.json';

import ErrorMessage from '../../../utils/MuiFields/ErrorMessage';

import { useFormikContext, Formik, Form } from 'formik';

import { useDialogForm } from '../../../contexts/DialogForm';

import { useFormData } from '../../../contexts/FormData';

import Field from '../../../utils/FormikFields/Field';

import { formatCurrency, getCCFee } from '../../../utils/util';

import { BasicFlex, LabelUpper } from '../../../utils/Common';

import { methods } from '../helpers';

import { useMutation, useQuery } from '@tanstack/react-query';

import { processPayment, getActiveGateways, getCCSettings, getTerminalStatus } from '../apiService';

import useSnackbarStore from '../../../store/snackbar';
import SimpleChip from '../../../utils/utilities/SimpleChip';


const ScanAnimationContainer = styled(BasicFlex)`
  max-height: 30rem;
  justify-content: center;
  align-items: center;
`

const TotalBar = styled(BasicFlex)`
  font-size: 3.5rem;
  text-align: center;
  margin: 1rem 0;
  gap: 2rem;
  font-weight: 500;
`

const TotalInfo = styled(BasicFlex)`
  flex-direction: column;
  align-items: flex-start;
  /* max-width: 95%; */
  width: auto;
  flex-grow: 1;
  margin: 0 auto;
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.$bg || props.theme.colors.lightBg};
  color: ${props => props.$color ? props.$color : "inherit"};
  border-radius: 12px;
  padding: 3rem 3rem;
  gap: .6rem;

  * {
    color: ${props => props.$color ? props.$color : "inherit"};
  }

`

const PaymentTypeHeader = styled.div`
  font-weight: 600;
  font-size: 1.4rem;
  margin-top: .5rem;
  margin-bottom: 1.4rem;
  text-align: center;
`;

const PaymentTypeTile = styled(BasicFlex)`
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 1rem;
  width: 12rem;
  height: 10rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.colors.lightBg};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.darkBg};
  }

  ${props => props.$disabled && css/* closed */`
    opacity: .5;
    cursor: not-allowed;
  `}

  ${props => props.$selected && css/* closed */`
    border-color: ${props.theme.colors.secondary};
    background-color: ${props.theme.colors.secondaryFaint};
    color: ${props.theme.colors.secondary};

    svg {
      color: ${props.theme.colors.secondary};
    }

    &:hover {
      background-color: ${props.theme.colors.secondaryFaint};
    }
  `}
`;

const ProcessNotice = styled.div`
  margin: 1rem auto;
  width: fit-content;
`;

const GatewayFormStyles = styled(Form)`
  width: 100%;
`;

const TerminalStyles = styled(BasicFlex)`
  border: ${props => props.$selected ? `2px solid ${props.theme.colors.secondary}` : `1px solid ${props.theme.colors.border}`};
  border-radius: 10px;
  height: 10rem;
  width: 15rem;
  align-items: center;
  position: relative;

  .name {
    font-weight: 600;
  }

  .id {
    color: ${props => props.theme.colors.midText};
    font-size: 1.2rem;
  }
`

const ActivityDot = styled.div`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  width: .8rem;
  height: .8rem;
  border-radius: 50%;
  background-color: ${props => props.$status === "inactive" ? props.theme.colors.lightText : props.$status === "loading" ? props.theme.colors.warning : props.theme.colors.success};
`;

/* ----- Magstripe reader ----- */

const SwipePanel = styled(BasicFlex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  min-height: 12rem;
  padding: 2rem;
  border-radius: 12px;
  border: 2px dashed ${props => props.$state === "captured" ? props.theme.colors.success : props.$state === "listening" ? props.theme.colors.secondary : props.theme.colors.border};
  background-color: ${props => props.$state === "captured" ? lighten(props.theme.colors.success, .92) : props.$state === "listening" ? props.theme.colors.secondaryFaint : props.theme.colors.lightBg};
  text-align: center;

  .title {
    font-weight: 600;
    font-size: 1.6rem;
  }

  .sub {
    color: ${props => props.theme.colors.midText};
    font-size: 1.3rem;
  }
`;

// Minimum length of a real swipe; anything shorter is a misread or stray keys
const MIN_SWIPE_LENGTH = 20;

// Display-only: pulls last 4 + cardholder name. Never send, store or log anything here.
const parseSwipeDisplay = (data = "") => {
  const track1 = data.match(/%B[\d*]*?([\d*]{4})\^([^^]*)\^/);
  if (track1) {
    const [last, first] = (track1[2] || "").split("/").map(s => s.trim());
    return { last4: track1[1], name: [first, last].filter(Boolean).join(" ") };
  }
  const track2 = data.match(/;[\d*]*?([\d*]{4})=/);
  if (track2) return { last4: track2[1], name: "" };
  return { last4: null, name: "" };
};

const MagstripeReader = ({ swipeData, setSwipeData, disabled }) => {
  const [listening, setListening] = useState(false);
  const [readError, setReadError] = useState(null);
  const bufferRef = useRef('');
  const timerRef = useRef(null);

  useEffect(() => {
    if (!listening) return;
    bufferRef.current = '';

    const finalize = () => {
      const data = bufferRef.current;
      bufferRef.current = '';
      if (data.length >= MIN_SWIPE_LENGTH) {
        setSwipeData(data);
        setReadError(null);
        setListening(false);
      } else if (data.length) {
        setReadError("Couldn't read the card. Please swipe again.");
      }
    };

    // Capture phase so the swipe never lands in a focused input or submits the form
    const onKeyDown = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'Escape') {
        setListening(false);
        return;
      }

      clearTimeout(timerRef.current);

      if (e.key === 'Enter') {
        // some readers send Enter between tracks — wait briefly for more data
        timerRef.current = setTimeout(finalize, 150);
        return;
      }

      if (e.key.length === 1) bufferRef.current += e.key;

      // a pause mid-swipe means stray keystrokes, not a card
      timerRef.current = setTimeout(() => { bufferRef.current = ''; }, 500);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(timerRef.current);
      bufferRef.current = '';
    };
  }, [listening, setSwipeData]);

  const clearSwipe = () => {
    setSwipeData(null);
    setReadError(null);
  };

  if (swipeData) {
    const { last4, name } = parseSwipeDisplay(swipeData);
    return (
      <SwipePanel $state="captured">
        <CheckCircleRoundedIcon color="success" fontSize="large" />
        <div className="title">Card captured{last4 ? ` · ending ${last4}` : ""}</div>
        {name ? <div className="sub">{name}</div> : null}
        <Button size="small" type="button" onClick={clearSwipe} disabled={disabled}>Clear &amp; swipe again</Button>
      </SwipePanel>
    );
  }

  if (listening) {
    return (
      <SwipePanel $state="listening">
        <Lottie lottieFile={ScanningAnimation} xy={{height: "10rem", width: "10rem"}} />
        <div className="title">Swipe card now…</div>
        {readError ? <ErrorMessage message={readError} /> : <div className="sub">Waiting for card reader</div>}
        <Button size="small" type="button" onClick={() => setListening(false)}>Cancel</Button>
      </SwipePanel>
    );
  }

  return (
    <SwipePanel $state="idle">
      <CreditCardRoundedIcon fontSize="large" color="secondary" />
      <Button variant="outlined" color="secondary" type="button" onClick={() => { setReadError(null); setListening(true); }} disabled={disabled}>
        Swipe Card
      </Button>
    </SwipePanel>
  );
};

/* ----- Edit-mode processing banner (replaces the tile selector) ----- */

const GATEWAY_ACCENT = "#2563eb";
const GATEWAY_BG = "#eef4ff";
const GATEWAY_BORDER = "#cfe0ff";

const EntryBanner = styled(BasicFlex)`
  align-items: center;
  gap: 1.4rem;
  width: 100%;
  border-radius: 12px;
  padding: 1.4rem 1.8rem;

  ${props => props.$variant === "gateway"
    ? css/* closed */`
        background-color: ${GATEWAY_BG};
        border: 1px solid ${GATEWAY_BORDER};
      `
    : css/* closed */`
        background-color: ${props.theme.colors.secondaryFaint};
        border: 1px solid ${props.theme.colors.border};
      `}
`;

const BannerIcon = styled(BasicFlex)`
  flex: 0 0 4.4rem;
  width: 4.4rem;
  height: 4.4rem;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background-color: #fff;
  color: ${props => props.$variant === "gateway" ? GATEWAY_ACCENT : props.theme.colors.secondary};

  svg {
    font-size: 2.4rem;
  }
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;

  .eyebrow {
    font-size: 1.1rem;
    font-weight: 500;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: ${props => props.theme.colors.midText};
    margin-bottom: .2rem;
  }

  .main {
    font-size: 1.5rem;
    font-weight: 700;
  }

  .main .accent {
    color: ${props => props.$variant === "gateway" ? GATEWAY_ACCENT : props.theme.colors.secondary};
  }
`;

const BannerPill = styled.div`
  margin-left: auto;
  padding: .5rem 1.1rem;
  border-radius: 999px;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #fff;
  background-color: ${props => props.$variant === "gateway" ? GATEWAY_ACCENT : props.theme.colors.secondary};
`;

const GATEWAY_NAMES = {
  sola: "Sola",
  banquest: "Banquest",
};

const GatewayPaymentInfo = styled.div`
  background-color: ${props => props.theme.colors.lightBg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: .5rem 1.5rem;
  width: 100%;
  border-radius: 8px;
  font-size: 1.6rem;
`

const GatewayPaymentInfoLine = styled(BasicFlex)`
  padding: 1.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.$light ?  props.theme.colors.mildText : "inherit"};

  &:last-child {
    border-bottom: none;
  }
`

const CCFeeComponentStyles = styled(BasicFlex)`
  opacity: ${props => props.$loading ? .5 : 1};
`;

const CCRateField = styled(Field)`
  width: 12rem;
`;

const CCTotal = styled.div`
  flex-grow: 1;
  background-color: ${props => lighten(props.theme.colors.info, .93)};
  border: 1px solid ${props => props.theme.colors.info};
  padding: .5rem 1.5rem;
  border-radius: 8px;
  margin-right: 2rem;
  /* color: ${props => props.theme.colors.mildText}; */
`;

const CCTotalAmount = styled.span`
  font-weight: 600;
  font-size: 1.6rem;
`

const CCFeeComponent = ({ }) => {
  const { values, errors, initialValues, setFieldValue } = useFormikContext() || {};

  const amount = values?.amount || 0;

  // enableReinitialize: the record can arrive after mount, so check initialValues too
  const isEdit = Boolean(values?.payment_id || initialValues?.payment_id);

  const { data: ccSettings, isLoading } = useQuery({
    queryKey: ['settings', 'cc', 'general'],
    queryFn: () => getCCSettings(),
  })

  const ccSetting = ccSettings?.data || {};

  // Re-sync the method baseline whenever Formik reinitializes (record loaded / form reset),
  // so a reinitialize never looks like a user changing the method.
  const initialValuesRef = useRef(initialValues);
  const prevMethodRef = useRef(values?.method);

  if (initialValuesRef.current !== initialValues) {
    initialValuesRef.current = initialValues;
    prevMethodRef.current = initialValues?.method;
  }

  useEffect(() => {
    if (isEdit || values?.method !== "credit_card") return;
    if (ccSetting?.use_cc_fee) {
      if (ccSetting?.amount_over === 0 || amount > ccSetting?.amount_over) {
        setFieldValue("use_cc_fee", true)
        setFieldValue("cc_rate", ccSetting?.default_cc_rate || 0.00)
      } else if (ccSetting?.amount_over > 0 && amount <= ccSetting?.amount_over) {
        setFieldValue("use_cc_fee", false)
        setFieldValue("cc_rate", 0.00)
      }
    }
  }, [ccSetting, amount, values?.method, isEdit])

  useEffect(() => {
    const prevMethod = prevMethodRef.current;
    prevMethodRef.current = values?.method;

    // mount, or a reinitialize — not a real user change
    if (prevMethod === values?.method) return;

    if (values?.method !== "credit_card") {
      setFieldValue("use_cc_fee", false)
      setFieldValue("cc_rate", 0.00)
    }
  }, [values?.method])

  const amountTotal = useMemo(() => {
    return amount + getCCFee(amount, values?.use_cc_fee ? values?.cc_rate : 0)
  }, [amount, values?.use_cc_fee, values?.cc_rate])

  if (values?.method !== "credit_card") return null;

  return (
    <CCFeeComponentStyles justify="flex-end" gap=".5rem" $loading={isLoading}>
      {values?.use_cc_fee ? <CCTotal>Total Amount Charged: <CCTotalAmount>${amountTotal}</CCTotalAmount></CCTotal> : null}
      <Field name="use_cc_fee" formFieldType="switch" label="Use CC Fee" size="small" />
      <CCRateField name="cc_rate" size="small" type="number" InputProps={{startAdornment: <PercentRoundedIcon fontSize="small" color="basic" sx={{mr: 1}} />}} />
      {isLoading ? <CircularProgress size={20} /> : null}
    </CCFeeComponentStyles>
  )
}

const PaymentType = ({ title, icon, type, entry, setEntry, disabled }) => {
  let selected = entry === type;
  const selectPaymentEntry = () => {
    if (disabled) return;
    setEntry(type);
  }

  return (
    <PaymentTypeTile $selected={selected} onClick={selectPaymentEntry} $disabled={disabled}>
      <div>{icon}</div> 
      <div>{title}</div>
    </PaymentTypeTile>
  )
}

const Terminal = ({ terminal, selectedTerminal, integration_id, setSelectedTerminal }) => {
  const { data: terminalStatus, isLoading } = useQuery({
    queryKey: ['terminalStatus', integration_id, terminal?.terminal_id, terminal?.name],
    queryFn: () => getTerminalStatus(integration_id, terminal?.terminal_id),
    enabled: terminal?.name === "Sola"
  })

  console.log(terminalStatus, "terminalStatus??????????????????????");

  let status = "active"

  if (terminal?.name === "Sola" && terminalStatus?.status !== 'CONNECTED') status = "inactive";

  return (
    <TerminalStyles dir="column" justify="center" gap=".5rem" width="auto" $selected={selectedTerminal === terminal?.terminal_id} onClick={() => setSelectedTerminal(terminal?.terminal_id)}>
      <ActivityDot $status={isLoading ? "loading" : status} />
      <div className="name">{terminal?.name}</div>
      <div className="id">{terminal?.terminal_id}</div>
    </TerminalStyles>
  )
}

// Fields the gateway needs before a charge can be sent
const PROCESS_REQUIRED_LABELS = {
  customer_id: "Customer",
  amount: "Amount",
};

const GatewayForm = ({ spreadPayments, dialog, invoice_total, balance, gateway, dateField }) => {

  const [processMethod, setProcessMethod] = useState("manual"); // manual, terminal, magstripe

  const [selectedTerminal, setSelectedTerminal] = useState(null);

  // raw swipe string — kept in component state only, never in Formik values
  const [swipeData, setSwipeData] = useState(null);

  const { values, errors, touched, setFieldValue, setErrors, setTouched } = useFormikContext() || {};
  const { formValues, setFormValues } = useFormData() || {};

  const showSnackBar = useSnackbarStore(state => state.showSnackbar);

  const { handleClose } = useDialogForm() || {};

  let notProcessed = errors?.processed;

  let default_terminal = gateway?.metadata?.terminals?.find(el => el?.default === true)?.terminal_id;

  const magstripeEnabled = gateway?.metadata?.magstripe === true;

  const showMethodToggle = Boolean(default_terminal || magstripeEnabled);

  // Default process method priority: terminal → magstripe → keyed
  useEffect(() => {
    setSelectedTerminal(default_terminal || null);

    if (default_terminal) setProcessMethod("terminal");
    else if (magstripeEnabled) setProcessMethod("magstripe");
    else setProcessMethod("manual");

    setSwipeData(null);
  }, [gateway, default_terminal, magstripeEnabled]);

  const changeProcessMethod = (e, val) => {
    if (!val) return; // exclusive group: clicking the active button sends null
    setProcessMethod(val);
    setSwipeData(null);
  }

  const applyDeposit = () => {
    let amount = invoice_total / 2;
    setFieldValue("amount", amount);
    spreadPayments({target: {value: amount}});
  }

  // Validates what the charge needs. Marks fields touched + sets Formik errors so
  // the fields themselves light up, and summarizes everything in the snackbar.
  const validateProcess = () => {
    const missing = {};

    if (!values?.customer_id) missing.customer_id = "Customer is required";

    const amount = Number(values?.amount);
    if (values?.amount === "" || values?.amount == null || isNaN(amount) || amount <= 0) {
      missing.amount = "Amount must be greater than 0";
    }

    if (processMethod === "terminal" && !selectedTerminal) missing.terminal = "Select a terminal";
    if (processMethod === "magstripe" && !swipeData) missing.magstripe = "Swipe a card first";

    const messages = Object.values(missing);
    if (!messages.length) return true;

    // only real Formik fields get touched/errors; terminal/magstripe are snackbar-only
    const formikMissing = Object.fromEntries(Object.entries(missing).filter(([k]) => k in PROCESS_REQUIRED_LABELS));
    if (Object.keys(formikMissing).length) {
      // shouldValidate=false so the schema run doesn't immediately wipe these errors
      setTouched({ ...touched, ...Object.fromEntries(Object.keys(formikMissing).map(k => [k, true])) }, false);
      setErrors({ ...errors, ...formikMissing });
    }

    showSnackBar(messages.join(" · "), 'error');
    return false;
  }

  // let balance = formValues?.final_balance || 0;

  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: processPayment,
    onSuccess: (data) => {
      let newPayment = {
        payment_id: data.payment_id,
        client_name: data.client_name,
        amount: data.amount,
        display_amount: data.display_amount,
        method: data.method,
        status: data.status,
        available_amount: data.available_amount,
        info: data.info
      }

      if (formValues?.new_payments) {
        setFormValues(ofv => {
          return {
            ...ofv,
            new_payments: ofv?.new_payments?.length ? [...ofv?.new_payments, newPayment] : [newPayment]
          }
        })
      }
      
      showSnackBar('Payment processed successfully!', 'success');

      if (handleClose) handleClose(); // new!

      if (data?.payment_id && !dialog) {
        navigate(`/payments/edit/${data.payment_id}`);
      }
    },
    onError: (e) => {
      console.log(e, "error");
      let message = e?.response?.data?.message
      showSnackBar(`Failed to process payment: ${message}`, 'error');
    },
    // never keep track data around after an attempt — a retry needs a fresh swipe
    onSettled: () => setSwipeData(null)
  });

  const process = async (e, terminal_id) => {
    // these buttons are type="submit" — stop Formik's submit from also firing
    e?.preventDefault?.();

    if (!validateProcess()) return;

    const isSwipe = processMethod === "magstripe";

    let packet = {
      customer_id: values.customer_id,
      client_name: formValues.client_name,
      amount: values.amount,
      terminal_id: terminal_id ? terminal_id : null,
      card_num: isSwipe ? null : values.card_num,
      exp: isSwipe ? null : values.exp,
      cvv: isSwipe ? null : values.cvv,
      magstripe: isSwipe ? swipeData : null,
      use_cc_fee: values.use_cc_fee,
      cc_rate: values.cc_rate,
      method: values.method,
      date: values.payment_date || values.date,
      entry: values.entry,
      info: values.info,
      checkout_id: values.checkout_id
    }

    mutate({packet});
    // navigate("/payments/edit/1");
  }

  return (
    <>
    {isPending ? <ScanAnimationContainer><Lottie lottieFile={ScanningAnimation} xy={{height: "30rem", width: "30rem"}} /></ScanAnimationContainer> : <>
      <Grid size={{xs: dateField ? 6 : 12}}>
      <Field name="amount" label="Payment Amount" type="number" size="small" blur={spreadPayments} />
    </Grid>
    {dateField ? <Grid size={{xs: 6}}>
      <Field name={values?.payment_date ? "payment_date" : "date"} label="Payment Date" formFieldType="date" size="small" />
    </Grid> : null}

      {showMethodToggle && <Grid size={{xs: 12}}>
        <ToggleButtonGroup size="small" color="secondary" fullWidth value={processMethod} onChange={changeProcessMethod} exclusive>
          <ToggleButton value="manual">Keyed Entry</ToggleButton>
          {default_terminal ? <ToggleButton value="terminal">Terminal</ToggleButton> : null}
          {magstripeEnabled ? <ToggleButton value="magstripe">Magstripe</ToggleButton> : null}
        </ToggleButtonGroup>
      </Grid>}

      {processMethod === "manual" && <><Grid size={{xs: 6}}>
        <Field name="card_num" 
        label="Credit Card Number" 
        formFieldType="card"
        size="small"
        placeholder="0000 0000 0000 0000"
         />
      </Grid>
      <Grid size={{xs: 12, md: 3}}>
        <Field 
          name="exp" 
          label="Expiration Date" 
          placeholder="MM/YY"
          formFieldType="mask" 
          maskFormat="##/##" 
          maskChar={['M', 'M', 'Y', 'Y']} 
        />
      </Grid>
      <Grid size={{xs: 12, md: 3}}>
        <Field name="cvv" label="CVV" type="number" size="small" />
      </Grid></>}

      {magstripeEnabled && processMethod === "magstripe" ? <Grid size={{xs: 12}}>
        <MagstripeReader swipeData={swipeData} setSwipeData={setSwipeData} disabled={isPending} />
      </Grid> : null}
      
      </>}

      {gateway?.metadata?.terminals?.length > 0 && processMethod === "terminal" ? <BasicFlex>
        {gateway?.metadata?.terminals?.map(el => <Terminal key={el.terminal_id} terminal={el} integration_id={gateway?.integration_id} selectedTerminal={selectedTerminal} setSelectedTerminal={setSelectedTerminal} />)}
      </BasicFlex> : null}

      <Grid size={{xs: 12}}>
        <CCFeeComponent />
      </Grid>

       {invoice_total || invoice_total === 0 ? <Grid size={{xs: 12}}>
      <div>
        <Button size="small" onClick={applyDeposit}>Select 50% Deposit</Button>
      </div>
      <TotalBar>
        <TotalInfo >
        <LabelUpper $normal $size="1.3rem">Invoice Total:</LabelUpper> 
        <div>{ formatCurrency(invoice_total) }</div>
        </TotalInfo>
        <TotalInfo $bg="#faece7" $color="#712b13">
        <LabelUpper $normal $size="1.3rem">Total Balance Due:</LabelUpper> 
        <div>{ formatCurrency((balance) || 0) }</div>
        </TotalInfo>
        </TotalBar>
      </Grid> : null}

      <Grid size={{xs: 12}}>
          {processMethod === "manual" ? <div style={{width: "100%"}}>
            <Button fullWidth variant="contained" type="submit" color="secondary" size="large" startIcon={<AccountBalanceWalletRoundedIcon />} onClick={process} loading={isPending}>Process Payment</Button>
            {notProcessed ? <ErrorMessage error={notProcessed} message="If Gateway is Selected, then payment must be processed first" /> : null}
          </div> : null}

          {default_terminal && processMethod === "terminal" ? <Button fullWidth variant="contained" type="submit" color="secondary" size="large" startIcon={<AccountBalanceWalletRoundedIcon />} onClick={(e) => process(e, selectedTerminal)} loading={isPending}>Process Through Terminal</Button> : null}

          {magstripeEnabled && processMethod === "magstripe" ? <div style={{width: "100%"}}>
            <Button fullWidth variant="contained" type="button" color="secondary" size="large" startIcon={<CreditCardRoundedIcon />} onClick={process} loading={isPending} disabled={!swipeData}>Process Swiped Card</Button>
            {notProcessed ? <ErrorMessage error={notProcessed} message="If Gateway is Selected, then payment must be processed first" /> : null}
          </div> : null}
      </Grid>
    </>
  )
}

const InfoField = ({ method, disabled = false }) => {
  let label = "";
  switch (method) {
    case "credit_card":
      label = "Last 4 digits of Credit Card";
      break;
    case "check":
      label = "Check Number";
      break;
    default:
      label = "Payment Info";
  }

  return (
    <Field name="info" label={label} size="small" disabled={disabled} />
  )
}

const ManualForm = ({ balance, method = "credit_card", spreadPayments, invoice_total, dateField }) => {

  const { values, setFieldValue } = useFormikContext() || {};
  
  const isEdit = !!values?.payment_id;
  const editDisabled = isEdit && values?.payment_entry_method === "gateway";

  const applyDeposit = () => {
    let amount = invoice_total / 2;
    setFieldValue("amount", amount);
    spreadPayments({target: {value: amount}});
  }

  return (
    editDisabled ? <GatewayPaymentInfo>
      <GatewayPaymentInfoLine $light>
        <div>Base Amount</div>
        <div>{formatCurrency(values?.amount)} </div>
      </GatewayPaymentInfoLine>
      <GatewayPaymentInfoLine $light>
        <div>CC Processing Fee ({values?.cc_rate}%)</div>
        <div>({formatCurrency(values?.cc_fee)})</div>
      </GatewayPaymentInfoLine>
      <GatewayPaymentInfoLine>
        <div>Charged Amount</div>
        <div>{formatCurrency(values?.display_amount)}</div>
      </GatewayPaymentInfoLine>
      <GatewayPaymentInfoLine>
        <div>Available Amount</div>
        <div>{formatCurrency(values?.available_amount)}</div>
      </GatewayPaymentInfoLine>
    </GatewayPaymentInfo> : <>
    <Grid size={{xs: dateField ? 6 : 12}}>
      <Field name="amount" label="Payment Amount" type="number" size="small" blur={spreadPayments} disabled={editDisabled} />
    </Grid>
    {dateField ? <Grid size={{xs: 6}}>
      <Field name={values?.payment_date ? "payment_date" : "date"} label="Payment Date" formFieldType="date" size="small" disabled={editDisabled} />
    </Grid> : null}
    <Grid size={{xs: 12, md: 6}}>
      <Field name="method" label="Payment Method" formFieldType="select" options={methods} size="small" disabled={editDisabled} />
    </Grid>
    <Grid size={{xs: 12, md: 6}}>
      <InfoField method={method} disabled={editDisabled} />
      <FormHelperText>Last 4 digits of Credit Card, etc.</FormHelperText>
    </Grid>
    <Grid size={{xs: 12}}>
      <CCFeeComponent />
    </Grid>
    {invoice_total || invoice_total === 0 ? <Grid size={{xs: 12}}>
      <div>
        <Button size="small" onClick={applyDeposit}>Select 50% Deposit</Button>
      </div>
      <TotalBar>
        <TotalInfo >
        <LabelUpper $normal $size="1.3rem">Invoice Total:</LabelUpper> 
        <div>{ formatCurrency(invoice_total) }</div>
        </TotalInfo>
        <TotalInfo $bg="#faece7" $color="#712b13">
        <LabelUpper $normal $size="1.3rem">Total Balance Due:</LabelUpper> 
        <div>{ formatCurrency((balance) || 0) }</div>
        </TotalInfo>
        </TotalBar>
    </Grid> : null}
    </>
  )
}

const EntryLabel = ({ entry, process_method }) => {
  // `process_method` is the source of truth (manual | gateway); fall back to
  // inferring from `entry` (sola | banquest) if it wasn't persisted.
  const isGateway = process_method === "gateway";
  const variant = isGateway ? "gateway" : "manual";
  const gatewayName = GATEWAY_NAMES[entry] || "Gateway";

  return (
    <EntryBanner $variant={variant}>
      <BannerIcon $variant={variant}>
        {isGateway ? <PointOfSaleRoundedIcon /> : <PaymentsRoundedIcon />}
      </BannerIcon>

      <BannerText $variant={variant}>
        <div className="eyebrow">Processed Via</div>
        <div className="main">
          {isGateway
            ? <>Gateway · <span className="accent">{gatewayName}</span></>
            : <><span className="accent">Manual</span> entry</>}
        </div>
      </BannerText>

      <BannerPill $variant={variant}>{isGateway ? "Gateway" : "Manual"}</BannerPill>
    </EntryBanner>
  );
};

const PaymentForm = ({ dialog = false, dateField = false }) => {

  const { values, setFieldValue } = useFormikContext() || {};

  let entry = values?.entry

  let isEdit = values?.payment_id ? true : false;

  const { formValues, setFormValues } = useFormData() || {};

  // only during invoice/checkout process
  let total_amount = values.total_amount

  let customer = formValues?.customer || values?.customer || {};
  let total_due = customer?.final_balance || 0;
  let invoice_total = values.balance;

  // console.log(customer, total_due, invoice_total, total_amount, values.balance, "customer, total_due, invoice_total");

  const { data: gateways } = useQuery({
    queryKey: ['gateways'],
    queryFn: () => getActiveGateways(),
  })

  let sola = gateways?.find(el => el.name === "sola");
  let banquest = gateways?.find(el => el.name === "banquest");

  // gateways this form can process through, in the order the integrations come back
  const supportedGateways = useMemo(
    () => (gateways || []).filter(el => GATEWAY_NAMES[el.name]),
    [gateways]
  );

  // Add mode: pre-select the first integration once gateways load.
  // Only sets entry/method — doesn't clear amount, so prefilled checkout amounts survive.
  const defaultEntryAppliedRef = useRef(false);
  useEffect(() => {
    if (isEdit || defaultEntryAppliedRef.current || !supportedGateways.length) return;
    defaultEntryAppliedRef.current = true;
    setFieldValue('entry', supportedGateways[0].name);
    setFieldValue('method', 'credit_card');
  }, [isEdit, supportedGateways]);

  const spreadPayments = (e) => {
  if (total_amount || total_amount === 0) return;
  let payment_amount = e.target.value;
  setFormValues(ofv => {
    if (!ofv.applied_payments?.length) return ofv;
      const newAppliedPayments = [...ofv.applied_payments.map(el => ({ ...el }))];
      newAppliedPayments.forEach(el => {
        el.selected = payment_amount > 0 ? true : false;
        el.amount = payment_amount > 0 ? Math.min(el.balance, payment_amount) : "";
        payment_amount -= el.amount;
      })
      return { ...ofv, applied_payments: newAppliedPayments };
    })
  }

   const selectEntryType = (val) => {
    if (isEdit && val === "sola") return;
    setFieldValue('entry', val);
    // clear payment values like amount, info, etc
    setFieldValue('amount', "");
    setFieldValue('info', '');
    setFieldValue('method', val === "manual" ? '' : 'credit_card');
    setFieldValue('cc_num', '');
    setFieldValue('cc_exp', '');
    setFieldValue('cc_cvv', '');
    setFieldValue('notes', '');
    spreadPayments({target: {value: ""}});
  }

  return (
    <Grid size={{xs: 12}}>
      <Card elevation={0} sx={{borderRadius: '12px'}}>
      <CardContent>
      
        <Grid container spacing={2}>
          {isEdit ? (
            <Grid size={{xs: 12}}>
              <EntryLabel entry={values?.gateway_name} process_method={values?.payment_entry_method} />
            </Grid>
          ) : (
            <Grid size={{xs: 12}}>
              <PaymentTypeHeader>Select Payment Processing Method</PaymentTypeHeader>
              <BasicFlex justify="center" gap="3rem">
                <PaymentType title="Manual" icon={<PaymentsRoundedIcon fontSize='large' />} entry={entry} setEntry={selectEntryType} type="manual" />
                {supportedGateways.map(gw => (
                  <PaymentType key={gw.name} title={GATEWAY_NAMES[gw.name]} icon={<PointOfSaleRoundedIcon fontSize='large' />} entry={entry} setEntry={selectEntryType} type={gw.name} disabled={isEdit} />
                ))}
              </BasicFlex>
              {!gateways?.length && !isEdit ? <BasicFlex justify="center" my=".5rem">
                <FormHelperText>To setup Credit Card processing, please contact admin</FormHelperText>
              </BasicFlex> : null}
            </Grid>
          )}

          {entry !== "manual" ? <Grid size={{xs: 12}} sx={{height: "2rem"}}>
            <ProcessNotice>
              <ErrorMessage message="Please Process Payment before saving" />
            </ProcessNotice>
          </Grid> : null}

          {entry === "manual" ? (
            <ManualForm balance={total_due} invoice_total={invoice_total} method={values.method} spreadPayments={spreadPayments} dateField={dateField} />
            ) : (
            <GatewayForm dialog={dialog} balance={total_due} gateway={entry === "sola" ? sola : entry === "banquest" ? banquest : null} invoice_total={invoice_total} spreadPayments={spreadPayments} dateField={dateField} />
          )}

          {dialog ? <Grid size={{xs: 12}}>
            <Field name="notes" label="Notes" formFieldType="text" minRows={4} multiline />
          </Grid> : null}
          
        </Grid>

      </CardContent>
      </Card>
      
    </Grid>
  )
};

export default PaymentForm;